<!-- eslint-disable -->

<template>
  <!-- Root div solves warning in console: -->
  <div class="root-edit-quote-container">
    <!-- Preloads images for print, otherwise they won't show up the first time -->
    <link rel="preload" href="/img/draft-watermark.png" as="image">
    <link rel="preload" href="/img/quote-header-bg-image.PNG" as="image">
    <link rel="preload" href="/img/Summit-Logo-RGB-Transparent.png" as="image">
    <QuoteUserMessages />
    <DetailsInfoBar :advancedSearch="advancedSearch"/>
    <DetailsNavBar
      :quote="quote"
      :advancedSearch="advancedSearch"
      :saveQuote="saveQuote"
      :saveAsNewQuote="saveAsNewQuote"
      :refetchQuote="refetchQuote"
      :loading="savingOrLoading"
    />
    <div class="quote-content">
      <div
        class="left-vertical-container pt-5"
        :style="{ scrollbarColor: scrollbarColors }"
      >
        <div class="quote-print-hidden">
          <DetailsHeaderForm
            v-show="onSalesInfoTab"
            :quote="quote"
            :sidePanelOpen="sidePanelOpen"
            :advancedSearch="advancedSearch"
            :isMissingRequiredMaterialData="isMissingRequiredMaterialData"
            :saveQuote="saveQuote"
            :refetchQuote="refetchQuote"
            @run-fetch-materials="handleContractSelected"
            @update:open-side-panel="openSidePanel($event)"
            @toggle="toggleSavingOrLoading"
          />
          <DetailsDataTable
            v-show="onQuoteTab"
            :quote="quote"
            :currency="
              quote?.currency || advancedSearch?.currency?.currencySearchString
            "
            :refetchQuote="refetchQuote"
            :sidePanelOpen="sidePanelOpen"
            :advancedSearch="advancedSearch"
            :selectedMaterialRef="selectedMaterialRef"
            :selectedActionRef="selectedActionRef"
            @update:open-side-panel="openSidePanel($event)"
            @toggle="toggleSavingOrLoading"
            @material-number-selected="handleMaterialNumberSelected"
            @description-selected="handleDescriptionSelected"
          />
        </div>
        <DetailsPrintHeader :quote="quote" :advancedSearch="advancedSearch" />
      </div>
      <div
        v-show="onQuoteTab"
        class="right-vertical-container side-panel-container quote-print-hidden"
        :class="{ open: sidePanelOpen, closed: !sidePanelOpen }"
        :style="{ scrollbarColor: scrollbarColors }"
      >
        <MaterialSidePanel
          ref="sidePanelRef"
          v-show="onQuoteTab"
          :quote="quote"
          :advancedSearch="advancedSearch"
          :sidePanelOpen="sidePanelOpen"
          :loading="savingOrLoading"
          :isMissingRequiredMaterialData="isMissingRequiredMaterialData"
          @update:open-side-panel="openSidePanel($event)"
          @update:toggle-side-panel="toggleSidePanel($event)"
          @update:selected-material="updateSelectedMaterial($event)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable */

import { ref, computed, reactive, watch, onMounted, nextTick, watchEffect, onBeforeUnmount, toRaw } from "vue";
import { useDebounceFn, watchOnce } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";

import type { QuotesSearch } from "@/components/quotes/types";
import MaterialSidePanel from "@/components/quotes/MaterialSidePanel.vue";
import DetailsInfoBar from "@/components/quotes/DetailsInfoBar.vue";
import DetailsNavBar from "@/components/quotes/DetailsNavBar.vue";
import DetailsHeaderForm from "@/components/quotes/DetailsHeaderForm.vue";
import DetailsDataTable from "@/components/quotes/DetailsDataTable.vue";
import DetailsPrintHeader from "@/components/quotes/DetailsPrintHeader.vue";
import QuoteUserMessages from "@/components/quotes/QuoteUserMessages.vue";
import {
  sidePanelActions as SidePanelActions,
  isEmpty,
  buildQuoteWellAddressQueryString,
  getMissingQuoteWellAddressFields,
  isQuoteEditorRoute,
  formatQuoteLineItemForSave,
  formatQuoteHeaderForSave,
  fixDate,
} from "@/utils/quoteHelpers";
import { useQuoteIdStore } from "@/store/quote_id";
import { useSidePanelStore } from "@/store/side_panel";
import { useSettingsStore } from "@/store/settings";

import {
  type SkMaterial,
  type Quotes,
  AllQuoteFieldsFragment as QuoteType,
  useGetQuoteByPkQuery,
  useSkWellAddressesQuery,
  useUpdateQuoteByPkMutation,
  useUpdateManyQuoteLineItemsMutation,
  useInsertQuoteMutation,
  useGetQuotesCountQuery,
} from "gql/api";

import { useRoute, useRouter } from "vue-router";
import useUserInfo from "@/composables/auth/useUserInfo";
import type { SpyglassUser } from "@/composables/auth/useUserInfo";


const { setQuoteId } = useQuoteIdStore();

const { executeMutation: insertQuote } = useInsertQuoteMutation();
const { executeMutation: updateManyQuoteLineItems } =
  useUpdateManyQuoteLineItemsMutation();
const { executeQuery: getQuotesCount } = useGetQuotesCountQuery({
  requestPolicy: "network-only",
  pause: true,
  variables: {}
});

const router = useRouter();
const route = useRoute();
const user = useUserInfo() as SpyglassUser;

if (user.is_customer) {
  nextTick().then(() => router.push({ name: "NotFound" }));
}

const toast = useToast();
const quoteId = computed(() => route.params?.quoteId)
const onSalesInfoTab = computed(() => route.path.endsWith('/salesinfo'));
const onQuoteTab = computed(() => route.path.endsWith('/quote'));

// const { quoteIdStored } = storeToRefs(useQuoteIdStore());
// const { quoteIdStore } = useQuoteIdStore();

const { panelWidth } = storeToRefs(useSidePanelStore());

const settingsStore = useSettingsStore();


const windowHeight = ref<number>(window.innerHeight);
const sidePanelRef = ref<InstanceType<typeof MaterialSidePanel> | null>(null);

/* Dev note: if you edit these, make sure your browser
   zoom level is at 100%, anything more/less will give
   you fractional element heights/widths
*/
const pageHeightsTable = {
  header: 65,
  headerGray: 25,
  infobar: 77,
  navbar: 62,
  footer: 79,
};
const maxHeightTable = computed(() => {
  // Table Ex.
  // (innerHeight - 1) - header - header2 - footer - margin = totalHeight
  // (1255 - 1)        - 65     - 25      - 79     - 36     =  1049px
  return (
    windowHeight.value -
    pageHeightsTable.header -
    pageHeightsTable.headerGray -
    pageHeightsTable.infobar -
    pageHeightsTable.navbar -
    pageHeightsTable.footer -
    1 +
    "px"
  );
});

const pageHeightsPanel = {
  header: 65,
  headerGray: 25,
  infobar: 77,
  navbar: 62,
  footer: 79
};
const maxHeightPanel = computed(() => {
  // Panel Ex.
  // (innerHeight - 1) - header - header2 - footer = totalHeight
  // (1255 - 1)        - 65     - 25      - 79     =
  return (
    windowHeight.value -
    pageHeightsPanel.header -
    pageHeightsPanel.footer -
    pageHeightsPanel.headerGray -
    pageHeightsPanel.infobar -
    pageHeightsPanel.navbar -
    1 +
    "px"
  );
});

const setHeightOnResize = (e: Event) => {
  // This function gets called many times on resize, but the actual ref
  // being set only happens once (using vue core debounce)
  windowHeight.value = window.innerHeight;
};

onMounted(() => {
  const setHeightDebounced = useDebounceFn(e => setHeightOnResize(e), 200);
  window.addEventListener("resize", setHeightDebounced);

  // Hide the "global" scrollbar - our children components
  // have their own isolated scrollbars.
  // TODO: investigate another solution for this
  document.body.style.overflow = 'hidden';
  // window.addEventListener("resize", (e) => setHeightOnResize(e))
});

onBeforeUnmount(() => {
  // Restore the global scrollbar when done with this DOM.
  document.body.style.overflow = '';
})

const fetchQuoteVariables = reactive<any>({
  id: quoteId.value
});

const {
  data: getQuote,
  executeQuery: fetchQuote,
  fetching: fetchingQuote
} = useGetQuoteByPkQuery({
  pause: quoteId.value === "new" || !quoteId.value,
  variables: fetchQuoteVariables
});

const refetchQuote = () => {
  // wrapper around fetchQuote
  return Promise.resolve()
    .then(() => fetchQuote())
    .then(resp => {
      if (resp?.error?.value) throw resp.error.value;
      return resp?.data?.value;
    });
};

const advancedSearch = reactive<QuotesSearch>({
  quoteSearch: "",
  nwaSearch: {
    nwaSearchString: "",
    name: null,
    id: null
  },
  skProposalNumber: null,
  userSearch: {
    userSearchString: "",
    id: null,
    display_name: null
  },
  priceListSearch: {
    priceListSearchString: "",
    name: null,
    id: null
  },
  contractSearch: {
    contractSearchString: "",
    number: null
  },
  salesDistrictSearch: {
    plantSearch: "",
    name: null,
    id: null
  },
  wellAddressSearch: {
    customerSearch: null,
    customer: { name: null, id: null },
    well: { name: null, uwi: null, id: null },
    address: {
      id: null,
      name: null,
      full_address: null,
      region: {
        code: null,
        country: { code: null }
      }
    }
  },
  pricingDate: new Date(),
  proposedStartDate: new Date(),
  currency: {
    currencySearchString: "",
    code: ""
  },
  versionNumber: 1,
  scenarioSearch: "",
  skJobType: null,
  skPlanningOrder: null,
  sk_job_number: null,
  sk_job_id: null,
  status: "",
  repName: "",
  repEmail: "",
  repPhone: "",
  note: "",
  comments: "",
  directions: "",
});

const quote = computed(() => {
  return getQuote?.value?.quotes_by_pk as QuoteType;
});

//const quoteRef = ref<QuoteType | null>(null);
//
//// We use watch effect to directly synchronize refs
//watchEffect(() => {
//  quoteRef.value = getQuote.value?.quotes_by_pk || null;
//});
//
//const quote = computed({
//  get() {
//    return quoteRef.value;
//  },
//  set(newVal: QuoteType | null) {
//    quoteRef.value = newVal;
//  }
//});

const sidePanelOpen = ref<boolean>(false);
const selectedMaterialRef = ref<SkMaterial>();
const selectedActionRef = reactive<Record<string, null | string>>({
  selected: null
});

const scrollbarColors = computed(() => {
  // format: "{thumb color} {background color}"
  return settingsStore.isDarkMode ? "white black" : "gray white";
});

const updateSelectedMaterial = (materialPayload: any) => {
  selectedMaterialRef.value = materialPayload.material;
  selectedActionRef.selected = materialPayload.action;

  /* Create a copy of the object to assign a new memory address / reference.

  This will allow the watcher in DetailsDataTable.vue to trigger for each
  subsequent add/replace if the selectedMaterialRef data is identical.
  */
  selectedMaterialRef.value = Object.assign({}, selectedMaterialRef.value)
};

const openSidePanel = (quoteId: string) => {
  // quote id comes from the "update:open-side-panel" emit in details header form
  if (quote?.value?.id || !isEmpty(quoteId)) {
    sidePanelOpen.value = true;

    handleSidePanelCSS();
  } else {
    toast.add({
      severity: "info",
      summary: "Please Create a Quote First.",
      detail: "Create a Quote to Search Materials.",
      life: 2500
    });
  }
};

const toggleSidePanel = (quoteId: string) => {
  if (quote?.value?.id || !isEmpty(quoteId)) {
    sidePanelOpen.value = !sidePanelOpen.value;
    
    handleSidePanelCSS();
  } else {
    toast.add({
      severity: "info",
      summary: "Please Create a Quote First.",
      detail: "Create a Quote to Search Materials.",
      life: 2500
    });
  }
};

const handleSidePanelCSS = () => {
  // Reset styles based on the new state
  const rightContainer = document.querySelector(".right-vertical-container") as HTMLElement;
  const sidePanel = document.querySelector(".side-panel") as HTMLElement;
  
  if (rightContainer && sidePanel) {
    if (!sidePanelOpen.value) {
      // Panel is now closed - reset to closed state
      rightContainer.style.width = '77px';
      sidePanel.style.width = '77px';
    } else {
      // Panel is now open 
      // Reset all styles
      rightContainer.style.cssText = '';
      sidePanel.style.cssText = '';
      
      // Allow a brief moment for the CSS to apply
      setTimeout(() => {
        sidePanel.style.boxSizing = 'border-box';
        rightContainer.style.boxSizing = 'border-box';
      }, 50);
    }
  }
}

const savingOrLoading = ref<boolean>(false);
const toggleSavingOrLoading = (isSaving: boolean) => {
  // TODO: Meant for emitting from child props, probably overkill
  savingOrLoading.value = isSaving;
}

const shouldFetchWellAddress = ref<boolean>(true);

// Restore the ship-to address, region, and country for quotes saved without
// them. Quotes generated from sizings between SGQ-139 and this fix were
// created with only the well ID, which disables Job Type and Quote Type
// because the country is missing. Values are filled in memory and saved with
// the next header save.
const { data: wellAddress } =
  useSkWellAddressesQuery({
    pause: computed(() => {
      if (!shouldFetchWellAddress.value) { return true; }

      const _quote = getQuote?.value?.quotes_by_pk;

      if (!_quote?.id) { return true; }

      if (["won", "rejected"].includes(_quote?.status)) { return true; }

      return !(
        _quote?.erp_customer_number &&
        (_quote?.erp_address_id || _quote?.erp_well_id) &&
        !_quote?.erp_well_country
      );
    }),
    requestPolicy: "network-only",
    variables: computed(() => {
      const _quote = getQuote?.value?.quotes_by_pk;

      return {
        summitQueryString: buildQuoteWellAddressQueryString({
          addressId: _quote?.erp_address_id,
          customerId: _quote?.erp_customer_number,
          wellId: _quote?.erp_well_id
        })
      };
    })
  });

watch(wellAddress, newValue => {
  const patch = getMissingQuoteWellAddressFields(
    quote.value,
    newValue?.getSKWellAddresses?.[0]
  );
  if (!patch) { return; }

  Object.assign(quote.value, patch);

  const address = advancedSearch.wellAddressSearch.address;
  address.id = address.id || patch.erp_address_id || null;
  address.name = address.name || patch.erp_address_name || null;
  address.full_address = address.full_address || patch.erp_full_address || null;
  address.region = {
    code: address.region?.code || patch.erp_well_region || null,
    country: { code: address.region?.country?.code || patch.erp_well_country || null }
  };
});

const { executeMutation: updateQuote } = useUpdateQuoteByPkMutation();

// watchOnce(wellAddress, (newValue) => {
//   shouldFetchWellAddress.value = false;
//   const resp = newValue?.getSKWellAddresses[0];

//   if (resp?.well) {
//     let shouldWellUpdateMessages = [];
//     const _quote = getQuote?.value?.quotes_by_pk;

//     const current_well_name = _quote?.erp_well_name;
//     const new_well_name = resp?.well?.name;

//     if (current_well_name !== new_well_name) {
//       shouldWellUpdateMessages.push(`Well name updated from SAP: ${current_well_name} to ${new_well_name}`);
//       quote.value.erp_well_name = new_well_name;
//     }

//     const new_well_uwi = resp?.well?.uwi;
//     const current_well_uwi = _quote?.erp_well_uwi;

//     if (current_well_uwi !== new_well_uwi) {
//       shouldWellUpdateMessages.push(`Well UWI updated from SAP: ${current_well_uwi} to ${new_well_uwi}`);
//       quote.value.erp_well_uwi = new_well_uwi;
//     }

//     const current_well_id = Number(_quote?.erp_well_id);
//     const new_well_id = Number(resp?.well?.id);

//     if (current_well_id !== new_well_id) {
//       shouldWellUpdateMessages.push(`Well ID updated from SAP: ${current_well_id} to ${new_well_id}`);
//       quote.value.erp_well_id = new_well_id;
//     }

//     const current_address_name = _quote?.erp_address_name;
//     const new_address_name = resp?.address?.name;

//     if (current_address_name !== new_address_name) {
//       shouldWellUpdateMessages.push(`Well Address Name updated from SAP: ${current_address_name} to ${new_address_name}`);
//       quote.value.erp_address_name = new_address_name;
//     }

//     const current_full_address = _quote?.erp_full_address;
//     const new_full_address = resp?.address?.full_address;
//     if (current_full_address !== new_full_address) {
//       shouldWellUpdateMessages.push(`Well Full Address updated from SAP: ${current_full_address} to ${new_full_address}`);
//       quote.value.erp_full_address = new_full_address;
//     }

//     const current_country_code = _quote?.erp_well_country;
//     const new_country_code = resp?.address?.region?.country?.code;

//     if (current_country_code !== new_country_code) {
//       shouldWellUpdateMessages.push(`Well Country updated from SAP: ${current_country_code} to ${new_country_code}`);
//       quote.value.erp_well_country = new_country_code;
//     }

//     const current_region_code = _quote?.erp_well_region;
//     const new_region_code = resp?.address?.region?.code;

//     if (current_region_code !== new_region_code) {
//       shouldWellUpdateMessages.push(`Well Region updated from SAP: ${current_region_code} to ${new_region_code}`);
//       quote.value.erp_well_region = new_region_code;
//     }

//     const current_customer_name = _quote?.erp_customer_name;
//     const new_customer_name = resp?.customer?.name;

//     if (current_customer_name !== new_customer_name) {
//       shouldWellUpdateMessages.push(`Customer name updated from SAP: ${current_customer_name} to ${new_customer_name}`);
//       quote.value.erp_customer_name = new_customer_name;
//     }

//     if (shouldWellUpdateMessages.length > 0) {
//       toast.add({
//         severity: "info",
//         summary: "Updating Quote Well Information",
//         detail: `SAP Information Updated, Updating Quote Well Information: ${shouldWellUpdateMessages.join(", ")}`,
//         life: 2500
//       });

//       // Update advancedSearch object
//       advancedSearch.wellAddressSearch = {
//         customerSearch: new_customer_name,
//         customer: {
//           name: new_customer_name,
//           id: resp?.customer?.id
//         },
//         well: {
//           name: new_well_name,
//           uwi: new_well_uwi,
//           id: new_well_id
//         },
//         address: {
//           id: resp?.address?.id,
//           name: new_address_name,
//           full_address: new_full_address,
//           region: {
//             code: new_region_code,
//             country: { code: new_country_code }
//           }
//         }
//       };
//     }
//   }
// });

const saveQuote = () => {
  if (!quote.value?.id) {
    // TODO: this function, when updating the quote,
    // doesn't like the use of quoteId.value. Forced to
    // use quote.value.id for now.
    return Promise.reject(new Error(`Quote missing ID.\nSee full quote object:\n${quote.value}`));
  }
  if (
    !advancedSearch?.quoteSearch ||
    advancedSearch?.quoteSearch === ""
  ) {
    toast.add({
      severity: "error",
      summary: "Quote Name Required",
      detail: "Please enter a quote name.",
      life: 2500
    });
    return Promise.reject(new Error("'Quote Name' field is empty, must be populated"));
  }

  const quoteId = quote?.value?.id
  const quoteObj = formatQuoteHeaderForSave(quote.value, advancedSearch);

  const createdLineItems = quote.value.quote_line_items
    .filter(lineItem => !lineItem.id)
    .map(item => formatQuoteLineItemForSave(item, quoteId));

  const updatedLineItems = quote.value.quote_line_items
    .filter(lineItem => lineItem.id)
    .map(item => formatQuoteLineItemForSave(item, quoteId))
    .map(item => {
      return {
        _set: item,
        where: {
          id: { _eq: item.id }
        }
      };
    });

  return updateQuote({
    id: quoteId,
    object: quoteObj,
    create_line_items: createdLineItems,
    update_line_items: updatedLineItems
  })
    .then(resp => {
      return resp;
    }).catch(err => {
      console.error(err)
      throw new Error(err);
    })
};

const defaultNestedFormat = () => {
  return {
    data: []
  };
};

const saveAsNewQuote = () => {
  return getQuotesCount().then(resp => {
    // TODO: fallback for undefined quotes aggregate count
    const proposal_count =
      1000001 + (resp?.data?.value?.quotes_aggregate?.aggregate?.count || 0);
    const skProposalNumber = `SG${proposal_count}`;
    const quoteName = advancedSearch.quoteSearch;
    const erpNwa = advancedSearch.nwaSearch;
    const user = advancedSearch.userSearch;
    const erpPriceList = advancedSearch.priceListSearch;
    const erpContract = advancedSearch.contractSearch;
    const erpPlant = advancedSearch.salesDistrictSearch;
    const erpWell = advancedSearch.wellAddressSearch;
    const pricingDate = advancedSearch.pricingDate;
    const proposedStartDate = advancedSearch.proposedStartDate;
    const currency = advancedSearch.currency;
    const versionNumber = advancedSearch.versionNumber;
    const spyglassScenario = advancedSearch.scenarioSearch;
    const skJobType = advancedSearch.skJobType;
    const status = advancedSearch.status;
    const note = advancedSearch.note;
    const quoteObj = {};
    Object.assign(quoteObj, {
      comments: "",
      currency: currency?.code,
      directions: "",
      erp_contract_number: erpContract?.number || null,
      erp_customer_name: erpWell?.customer?.name,
      erp_customer_number: erpWell?.customer?.id,
      erp_nwa_id: erpNwa?.id,
      erp_nwa_name: erpNwa?.name,
      erp_plant_id: erpPlant?.id,
      erp_plant_name: erpPlant?.name,
      erp_price_list_name: erpPriceList?.name,
      erp_price_list_number:
        erpPriceList?.number === undefined ||
        erpPriceList?.number === "undefined" ? null : erpPriceList.number,
      erp_well_country: erpWell?.address?.region?.country?.code,
      erp_well_region: erpWell?.address?.region?.code,
      erp_well_name: erpWell?.well?.name,
      erp_well_uwi: erpWell?.well?.uwi,
      erp_well_id: erpWell?.well?.id,
      erp_address_id: erpWell?.address?.id,
      erp_address_name: erpWell?.address?.name,
      erp_full_address: erpWell?.address?.full_address,
      note: note,
      prepared_by_id: user?.id,
      customer_rep_name: advancedSearch?.repName || "",
      customer_rep_email: advancedSearch?.repEmail || "",
      customer_rep_phone_number: advancedSearch?.repPhone || "",
      // Hasura enforces a date for insertQuote, so we need to provide a default
      // one if the user clears it out beforehand. Will error out if "".
      pricing_date:
        fixDate(pricingDate) || fixDate(new Date()),
      proposed_start_date:
        fixDate(proposedStartDate) || fixDate(new Date()),
      sk_proposal_number: skProposalNumber,
      spyglass_scenario_import_json: JSON.stringify(spyglassScenario),
      spyglass_scenario_id: spyglassScenario?.id,
      sk_job_type: skJobType,
      status,
      version_number: versionNumber,
      name: quoteName,
      source: "user",
      quote_line_items: defaultNestedFormat()
    });
    return insertQuote({ object: quoteObj })
      .then(resp => {
        const quoteIdResp = resp?.data?.insert_quotes_one?.id;

        if (quoteIdResp) {
          router.push({
            name: "EditQuote",
            params: {
              quoteId: quoteIdResp
            }
          })
          .then(() => {
            // TODO find a way to remove this for save as new
            //      tip: check add new version for bugs after
            return refetchQuote()
          });
        }

        setQuoteId(quoteIdResp);
        return quoteIdResp;
      })
  });
};

const handleMaterialNumberSelected = (materialNumber: string) => {
  if (sidePanelRef.value) {
    sidePanelRef.value.handleNewMaterialNumber(materialNumber);
  }
}

const handleDescriptionSelected = (description: string) => {
  if (sidePanelRef.value) {
    sidePanelRef.value.handleNewDescription(description);
  }
};

const handleContractSelected = () => {
  if (sidePanelRef.value) {
    sidePanelRef.value.handleNewContract();
  }
}

watch(quoteId, (newId, oldId) => {
  // TODO: fetchingQuotes variables does not always work,
  // thus forced to watch quoteId.
  // quoteId needs to be continuously watched in the following scenarios:
  //    1) save as new
  //    2) version create
  //    3) version change
  //    4) scenario select
  // Leaving the editor clears the route's quoteId (SK-5222). Keep the current
  // quote instead of refetching with an empty id and blanking the page.
  if (!isQuoteEditorRoute(route)) {
    return;
  }
  fetchQuoteVariables.id = newId
})

watch(quote, (newQuote, oldQuote) => {
  // TODO: rethink how quotes are deconstructed (which is
  // what's creating different references) or get around to using Pinia.
  if (quote.value !== null) {
    advancedSearch.skPlanningOrder = newQuote?.sk_planning_order || null;
    advancedSearch.quoteSearch = newQuote?.name || "";
    advancedSearch.skProposalNumber =
      newQuote?.sk_proposal_number || null;

    advancedSearch.userSearch = {
      userSearchString: newQuote?.user?.display_name || "",
      id: newQuote?.user?.id || null,
      display_name: newQuote?.user?.display_name || ""
    };

    advancedSearch.contractSearch = {
      number: newQuote?.erp_contract_number || null,
      contractSearchString: newQuote?.erp_contract_number || ""
    };

    advancedSearch.salesDistrictSearch = {
      plantSearch: newQuote?.erp_plant_name || "",
      name: newQuote?.erp_plant_name || "",
      id: newQuote?.erp_plant_id || null
    };

    advancedSearch.nwaSearch = {
      nwaSearchString: newQuote?.erp_nwa_name || "",
      id: newQuote?.erp_nwa_id || null,
      name: newQuote?.erp_nwa_name || ""
    };

    advancedSearch.priceListSearch = {
      priceListSearchString: newQuote?.erp_price_list_name || "",
      name: newQuote?.erp_price_list_name || "",
      number: newQuote?.erp_price_list_number === undefined || newQuote?.erp_price_list_number === "undefined" ? undefined : newQuote?.erp_price_list_number
    };

    if (
      advancedSearch.wellAddressSearch.well.id === null &&
      advancedSearch.wellAddressSearch.address.id === null &&
      advancedSearch.wellAddressSearch.customerSearch === null &&
      advancedSearch.wellAddressSearch.customer.id === null
    ) {
      /* TODO: this is a bandaid for a symptom of a bigger problem.

      Problem: the well (wellAddressSearch) is reverting after:
          1) Having a certain scenario selected,
          2) changing to a certain well, and
          3) several network requests kicking off and changing hands
             with advancedSearch

      Solution: only assign the wellAddressSearch in this watcher once
      */
      advancedSearch.wellAddressSearch = {
        customerSearch: newQuote?.erp_customer_name,
        customer: {
          name: newQuote?.erp_customer_name,
          id: newQuote?.erp_customer_number
        },
        well: {
          name: newQuote?.erp_well_name,
          uwi: newQuote?.erp_well_uwi,
          id: newQuote?.erp_well_id
        },
        address: {
          id: newQuote?.erp_address_id,
          name: newQuote?.erp_address_name,
          full_address: newQuote?.erp_full_address,
          region: {
            code: newQuote?.erp_well_region,
            country: { code: newQuote?.erp_well_country }
          }
        }
      };
    }

    advancedSearch.repName = newQuote?.customer_rep_name || "";
    advancedSearch.repEmail = newQuote?.customer_rep_email || "";
    advancedSearch.repPhone = newQuote?.customer_rep_phone_number || "";

    advancedSearch.pricingDate = newQuote?.pricing_date
      ? new Date(newQuote?.pricing_date.replaceAll("-", "/") || "")
      : null;
    advancedSearch.proposedStartDate = newQuote?.proposed_start_date
      ? new Date(newQuote?.proposed_start_date.replaceAll("-", "/") || "")
      : null;
    // If the date string contains dashes, primevue seems to incorrectly interpret this by setting the day off by 1
    // As a temporary workaround, I replaced the dashes with slashes
    advancedSearch.currency = {
      currencySearchString: newQuote?.currency || null,
      name: newQuote?.currency || null
    };
    advancedSearch.skJobType = newQuote?.sk_job_type || null;
    advancedSearch.scenarioSearch = newQuote?.quote_scenario?.title;
    advancedSearch.versionNumber = newQuote?.version_number || 1;
    advancedSearch.sk_job_number = newQuote?.sk_job_number;
    advancedSearch.sk_job_id = newQuote?.sk_job_id;

    advancedSearch.note = newQuote?.note || "";
    advancedSearch.comments = newQuote?.comments || "";
    advancedSearch.directions = newQuote?.directions || "";
  }
});

const isMissingRequiredMaterialData = computed(() => {
  /* Must be defined before the pause variable to be (re)used
  *
  * "Required" here means required by the SK API. If you replace any of the below
  * URL parameters with what I specified below, you will get a server error.
  *
  * Ultimately, we are circumventing "Bad Request" and "INTERNAL SERVER ERROR".
  *
  * - Lawrence
  */
  return (
    quote.value?.id === "new"
    || !quote.value?.id
    || !advancedSearch?.priceListSearch?.number
    || advancedSearch?.priceListSearch?.number === "undefined"
    || !advancedSearch?.wellAddressSearch?.address?.region?.country?.code
    || !advancedSearch?.salesDistrictSearch?.id
    || !advancedSearch?.pricingDate
  )
});

</script>

<style scoped lang="scss">

.left-vertical-container {
  /*max-height: 100%;*/
  max-height: v-bind(maxHeightTable);
  /*max-height: "1000px";*/
  z-index: 1;
  padding-left: 3em; /* Fixes x-axis scroll bar */
  padding-right: 1.5em; /* Fixes x-axis scroll bar */
  @media only screen and (max-width: 480px) {
    // todo: revise?
    margin-left: 0em;
    margin-right: 0em;
  }
  width: 100%;
  height: 100vh;
}

.right-vertical-container {
  box-sizing: border-box;
  overflow-y: scroll;
  margin: 0;
  padding: 0;
  /*max-height: 100%;*/
  height: v-bind(maxHeightPanel);
  /*max-height: "1000px";*/
  z-index: 1;
  flex-grow: 0;
  flex-shrink: 0;
  background-color: var(--gray-500);
  transition: width 0.3s ease;
}

@media screen {
  .quote-print-shown {
    display: none;
  }

  .left-vertical-container {
    overflow-y: scroll;
  }
}

@media print {
  @page {
    margin: 0.75cm;
    width: 8.5in;
  }

  .quote-print-hidden {
    display: none !important;
  }

  .left-vertical-container {
    margin: 0.5rem !important;
  }
}

.right-vertical-container.side-panel-container.open {
  /* TODO (mobile) Consider on small screen sizes, remove min width here */
  width: v-bind(panelWidth);
  box-sizing: border-box;
}

.right-vertical-container.side-panel-container.closed {
  /* TODO (mobile) Consider on small screen sizes, remove min width here */
  width: 77px;
  overflow-y: hidden;
  /*max-width: 77px;*/
}

.quote-content {
  display: flex;
}
</style>
